// Browser-safe catalog of the selectable gigs (Scenario 2): id, label, blurb,
// the data-entry field spec, and the conformance teaching hint.
//
// This module imports NOTHING heavy — no profile/schema JSON, no seal path, no
// Node built-ins — so both the browser UI and the Pages Function can import it.
// It is the single source of truth for gig ids and input shape:
//   - the UI renders the data-entry form from `fields`,
//   - /api/run validates the submitted inputs against `fields`,
//   - src/live/gigs.ts (the seal-bound gig objects) reuses these ids/labels.
// gigs.ts holds the matching seal logic; a test asserts every catalog id
// resolves to a gig so the two cannot drift.

export type GigFieldType = "number" | "text" | "csv" | "textarea" | "boolean" | "select";

export interface GigField {
  key: string;
  label: string;
  type: GigFieldType;
  // String form for the input control (numbers are parsed; csv is split on
  // commas; textarea is a multi-line string; boolean is "true"/"false";
  // select is one of `options`).
  default: string;
  help?: string;
  min?: number;
  // For "select": the closed set of allowed values. The Function rejects any
  // value not in this list, so a select field can only ever seal a controlled
  // value (never free-typed PHI). Ignored for other field types.
  options?: string[];
}

export interface GigCatalogEntry {
  id: string;
  label: string;
  blurb: string;
  // A review gig makes one real Claude call; the editable review instruction
  // applies only to these.
  usesReview: boolean;
  fields: GigField[];
  // What input flips the sealed receipt to conformant:false, for the UI hint.
  violationHint: string;
  // Optional per-gig submit-button label (e.g. "Submit prior authorization",
  // "Scan record"). Falls back to the scenario's generic run label.
  runLabel?: string;
}

// Caps mirror the seal-side gig params (invoice/expense max_amount, the
// prior-auth payer allowlist). Kept here only as human-readable hint copy.
export const GIG_CATALOG: GigCatalogEntry[] = [
  {
    id: "invoice_approval",
    label: "Accounts payable: vendor invoice review",
    blurb:
      "The agent reviews a vendor invoice against a $5,000 auto-pay authority, gets a real model review, and seals a payment instruction.",
    usesReview: true,
    fields: [
      { key: "amountUsd", label: "Invoice amount (USD)", type: "number", default: "1850", min: 0 },
      { key: "vendor", label: "Vendor", type: "text", default: "Summit Medical Supply Co." },
      { key: "invoiceId", label: "Invoice id", type: "text", default: "INV-2026-0847" }
    ],
    violationHint:
      "Set the amount over $5,000 to seal a receipt that honestly records conformant: false."
  },
  {
    id: "expense_reimbursement",
    label: "Finance: employee expense reimbursement",
    blurb:
      "The agent reviews an employee expense report against a $2,500 per-diem cap and authorizes the reimbursement, bound to the same report and amount it reviewed.",
    usesReview: false,
    fields: [
      {
        key: "amountUsd",
        label: "Reimbursement amount (USD)",
        type: "number",
        default: "1200",
        min: 0
      },
      {
        key: "expenseReportRef",
        label: "Expense report reference",
        type: "text",
        default: "exp-report-2026-0412",
        help: "An opaque id/hash, not the report contents."
      },
      {
        key: "perDiemPolicyRef",
        label: "Per-diem policy reference",
        type: "text",
        default: "per-diem-v3"
      },
      {
        key: "authorizedBy",
        label: "Authorized by",
        type: "text",
        default: "d.alvarez@torbridge.example"
      }
    ],
    violationHint: "Set the amount over $2,500 to seal a receipt that records conformant: false."
  }
];

export function catalogEntry(id: string | undefined): GigCatalogEntry | undefined {
  if (id === undefined) return GIG_CATALOG[0];
  return GIG_CATALOG.find((g) => g.id === id);
}
